export interface LogFormatterConfig {
  maxMessageLength?: number
  enableSanitization?: boolean
}

export class LogFormatter {
  static create(config?: LogFormatterConfig): LogFormatter {
    return new LogFormatter(config)
  }

  constructor(private config: LogFormatterConfig = {}) {}

  /**
   * Formats a message to string, handling different input types
   */
  formatMessage(message: unknown): string {
    if (typeof message === 'string') {
      return message
    }

    if (message instanceof Error) {
      return message.message
    }

    if (typeof message === 'object' && message !== null) {
      return this.serializeSafe(message)
    }

    return String(message)
  }

  /**
   * Safely serializes objects to JSON without circular references
   */
  serializeSafe(obj: unknown): string {
    const seen = new WeakSet()

    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]'
        }
        seen.add(value)
      }

      // Handle special cases
      if (typeof value === 'function') {
        return '[Function]'
      }

      if (typeof value === 'undefined') {
        return undefined // Keep undefined as undefined in JSON
      }

      if (value instanceof Date) {
        return value.toISOString()
      }

      if (value instanceof RegExp) {
        return value.toString()
      }

      return value
    })
  }

  /**
   * Formats an Error object into a structured object
   */
  formatError(error: Error): object {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause ? this.formatError(error.cause as Error) : undefined,
    }
  }

  /**
   * Truncates a message if it exceeds the maximum length
   */
  truncateMessage(message: string, maxLength: number): string {
    if (message.length <= maxLength) {
      return message
    }

    const truncated = message.substring(0, maxLength - 3)
    return `${truncated}...`
  }

  /**
   * Formats metadata safely, handling circular references and special types
   */
  formatMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
    return Object.entries(metadata).reduce(
      (formatted, [key, value]) => {
        if (value instanceof Error) {
          return { ...formatted, [key]: this.formatError(value) }
        }
        if (typeof value === 'object' && value !== null) {
          return { ...formatted, [key]: JSON.parse(this.serializeSafe(value)) }
        }
        return { ...formatted, [key]: value }
      },
      {} as Record<string, unknown>
    )
  }

  /**
   * Sanitizes log message by removing potentially sensitive information
   */
  sanitizeMessage(message: string): string {
    const shouldSanitize = this.config.enableSanitization ?? true

    if (!shouldSanitize) {
      return message
    }

    // Remove potential passwords, tokens, and keys
    return message
      .replace(/password\s*[=:]\s*[^\s&]*/gi, 'password=***')
      .replace(/token\s*[=:]\s*[^\s&]*/gi, 'token=***')
      .replace(/key\s*[=:]\s*[^\s&]*/gi, 'key=***')
      .replace(/secret\s*[=:]\s*[^\s&]*/gi, 'secret=***')
      .replace(/authorization\s*[=:]\s*[^&]*/gi, 'authorization=***')
  }
}
